import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Sparkles, CheckCircle2, X, TrendingDown, TrendingUp, Minus, AlertTriangle } from 'lucide-react';
import { formatClassLabel } from '@/constants/classes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const severityStyles = {
  critical: { badge: 'bg-red-600 text-white', card: 'border-red-200 bg-red-50/50', label: 'CRITICAL' },
  high:     { badge: 'bg-orange-500 text-white', card: 'border-orange-200 bg-orange-50/50', label: 'NEEDS HELP' },
  medium:   { badge: 'bg-yellow-500 text-white', card: 'border-yellow-200 bg-yellow-50/40', label: 'EARLY WARNING' },
};

const TrendIcon = ({ trend }) => {
  if (trend === 'declining') return <TrendingDown className="w-3.5 h-3.5 text-red-600" />;
  if (trend === 'improving') return <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />;
  return <Minus className="w-3.5 h-3.5 text-gray-400" />;
};

const TeacherPracticeSuggestions = ({ embedded = false, onCountChange }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ suggestions: [], total: 0, critical_count: 0, high_count: 0, medium_count: 0 });
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/teacher/practice-suggestions`);
      setData(res.data);
      onCountChange?.(res.data.total);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const key = (s) => `${s.student_id}::${s.chapter_name}`;

  const approve = async (s) => {
    setBusyId(key(s));
    try {
      await axios.post(`${API}/teacher/practice-suggestions/approve`, {
        student_id: s.student_id,
        chapter_name: s.chapter_name,
        subject: s.subject,
        class_level: s.class_level,
        title: s.suggested_title,
        note: s.suggested_note,
      });
      toast.success(`Practice assigned to ${s.student_name}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally {
      setBusyId(null);
    }
  };

  const skip = async (s) => {
    setBusyId(key(s));
    try {
      await axios.post(`${API}/teacher/practice-suggestions/skip`, {
        student_id: s.student_id,
        chapter_name: s.chapter_name,
      });
      toast.info('Dismissed for 14 days');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to skip');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'space-y-4'} data-testid="practice-suggestions-section">
      {!embedded && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-5 text-white shadow-md">
          <div className="flex items-center gap-3">
            <Sparkles className="w-8 h-8" />
            <div className="flex-1">
              <h2 className="text-xl font-bold">AI Practice Suggestions</h2>
              <p className="text-purple-100 text-sm">
                Students who need extra practice based on their last 5 test attempts. Approve to send them targeted practice; skip to dismiss for 14 days.
              </p>
            </div>
            <Button variant="secondary" onClick={load} className="bg-white/20 hover:bg-white/30 text-white border-0">
              Refresh
            </Button>
          </div>
          {data.total > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="bg-white/10 rounded-lg px-3 py-2">
                <p className="text-xs text-purple-100">Critical</p>
                <p className="text-2xl font-bold">{data.critical_count}</p>
              </div>
              <div className="bg-white/10 rounded-lg px-3 py-2">
                <p className="text-xs text-purple-100">Needs help</p>
                <p className="text-2xl font-bold">{data.high_count}</p>
              </div>
              <div className="bg-white/10 rounded-lg px-3 py-2">
                <p className="text-xs text-purple-100">Early warning</p>
                <p className="text-2xl font-bold">{data.medium_count}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {data.total === 0 ? (
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-emerald-900">All students are tracking well!</h3>
            <p className="text-sm text-emerald-700 mt-1">No struggling students detected from recent test attempts.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="practice-suggestions-list">
          {data.suggestions.map((s) => {
            const sty = severityStyles[s.severity] || severityStyles.medium;
            const isBusy = busyId === key(s);
            return (
              <Card key={key(s)} className={`${sty.card} border-2`} data-testid={`suggestion-${s.student_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-base font-bold text-gray-900">{s.student_name}</h3>
                        <Badge variant="outline" className="text-xs">{formatClassLabel(s.class_level)}{s.section || ''}</Badge>
                        <Badge variant="outline" className="text-xs">Roll {s.roll_number}</Badge>
                        <Badge className={`text-[10px] ${sty.badge}`}>{sty.label}</Badge>
                      </div>
                      <div className="text-sm text-gray-700 flex items-center gap-3 flex-wrap">
                        <span className="flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5 text-orange-600" /><b>{s.subject}</b> · {s.chapter_name}</span>
                        <span className="flex items-center gap-1"><TrendIcon trend={s.trend} /> {s.trend}</span>
                        <span>Avg <b>{s.avg_score}%</b> over {s.attempts_count} tests</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{s.reason}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        size="sm"
                        onClick={() => approve(s)}
                        disabled={isBusy}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`approve-${s.student_id}`}
                      >
                        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" />Assign Practice</>}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => skip(s)}
                        disabled={isBusy}
                        data-testid={`skip-${s.student_id}`}
                      >
                        <X className="w-4 h-4 mr-1" /> Skip
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeacherPracticeSuggestions;
